import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { formatApiError } from "@/lib/api";
import { Activity, Lock, User } from "lucide-react";

const SAVED_KEY = "rs_saved_login";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Pre-fill from the last successful login to speed things up.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(SAVED_KEY) || "null");
      if (saved?.email) {
        setEmail(saved.email);
        setPassword(saved.password || "");
      }
    } catch {
      /* ignore corrupt cache */
    }
  }, []);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      localStorage.setItem(SAVED_KEY, JSON.stringify({ email, password }));
      navigate(u.role === "admin" ? "/dashboard" : "/my-schedule");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rs-grid-bg rs-scanlines min-h-screen flex items-center justify-center p-4 relative">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: "url(https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg)",
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-[#05060A]/80" />

      <div className="relative z-10 w-full max-w-md rs-fade-up">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Activity className="w-8 h-8 text-[#D902EE] rs-text-glow-magenta" />
          <h1 className="font-mono-rs text-3xl font-extrabold tracking-tighter uppercase text-white">
            Radio<span className="text-[#D902EE] rs-text-glow-magenta">Shift</span>
          </h1>
        </div>

        <div className="bg-[#0F1219] border border-[#D902EE]/30 p-8 rs-glow-magenta">
          <p className="text-xs tracking-[0.25em] uppercase text-[#00F0FF] mb-6 font-mono-rs">
            // Radiology Scheduling Terminal
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-2 block">Email</label>
              <div className="flex items-center border border-slate-700 bg-[#05060A] focus-within:border-[#00F0FF] transition-colors duration-300">
                <User className="w-4 h-4 text-slate-500 ml-3" />
                <input
                  data-testid="login-email-input"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required autoComplete="username"
                  className="w-full bg-transparent px-3 py-3 text-sm text-white outline-none"
                  placeholder="you@hospital.org"
                />
              </div>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-2 block">Password</label>
              <div className="flex items-center border border-slate-700 bg-[#05060A] focus-within:border-[#00F0FF] transition-colors duration-300">
                <Lock className="w-4 h-4 text-slate-500 ml-3" />
                <input
                  data-testid="login-password-input"
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required autoComplete="current-password"
                  className="w-full bg-transparent px-3 py-3 text-sm text-white outline-none"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-[#FF003C] text-sm border border-[#FF003C]/40 bg-[#FF003C]/10 px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit" disabled={loading}
              className="w-full font-mono-rs tracking-[0.2em] uppercase text-sm py-3 border border-[#D902EE] bg-[#D902EE]/10 text-white hover:bg-[#D902EE]/25 hover:rs-glow-magenta transition-all duration-300 disabled:opacity-50"
            >
              {loading ? "Authenticating…" : "Access Terminal"}
            </button>
          </form>
        </div>

        <div className="mt-6 flex items-center justify-center gap-4 text-[10px] tracking-[0.15em] uppercase font-mono-rs">
          <Link data-testid="login-donate-link" to="/donate" className="text-slate-500 hover:text-[#D902EE] transition-colors">Donate</Link>
          <span className="text-slate-700">/</span>
          <Link data-testid="login-support-link" to="/support" className="text-slate-500 hover:text-[#00F0FF] transition-colors">Support</Link>
          <span className="text-slate-700">/</span>
          <Link data-testid="login-privacy-link" to="/privacy" className="text-slate-500 hover:text-[#00F0FF] transition-colors">Privacy Policy</Link>
        </div>

        <p data-testid="login-company" className="mt-5 text-center text-[10px] tracking-[0.15em] uppercase font-mono-rs text-slate-600">
          Built by GSoftware LLC
        </p>
      </div>
    </div>
  );
}
